import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, AlertTriangle, CalendarX, TrendingDown, GraduationCap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Chip = ({ icon: Icon, label, value, tone = 'amber', testId }) => {
  if (!value) return null;
  const cls = {
    amber: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700',
    red: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-200 dark:border-red-700',
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium border px-2 py-1 rounded-full ${cls}`} data-testid={testId}>
      <Icon className="w-3 h-3" /> {label}: <strong>{value}</strong>
    </span>
  );
};

/**
 * Phase 72.C — P2 dashboard chip row.
 * Admin + Staff only. Re-uses /api/academic/overview (already RBAC-scoped backend-side).
 * Only renders when at least one academic attention signal is non-zero.
 */
const DashboardAcademicAttention = () => {
  const [att, setAtt] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/academic/overview`)
      .then(r => { if (alive) setAtt(r.data?.attention || null); })
      .catch(() => { if (alive) setAtt(null); });
    return () => { alive = false; };
  }, []);

  if (!att) return null;
  const total = (att.needs_attention || 0) + (att.at_risk || 0)
    + (att.recent_absent_or_no_show || 0) + (att.low_average_grade || 0)
    + (att.missing_attendance_this_week || 0);
  if (total === 0) return null;

  return (
    <button
      type="button"
      onClick={() => navigate('/academic')}
      className="w-full text-left flex flex-wrap items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2 hover:shadow-sm transition-shadow"
      data-testid="dashboard-academic-attention"
    >
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200">
        <GraduationCap className="w-3.5 h-3.5 text-emerald-600" /> Academic Attention
      </span>
      <Chip icon={AlertCircle} label="Needs Attention" value={att.needs_attention} tone="amber" testId="dash-att-needs" />
      <Chip icon={AlertTriangle} label="At Risk" value={att.at_risk} tone="red" testId="dash-att-risk" />
      <Chip icon={CalendarX} label="Recent Absent/No-Show" value={att.recent_absent_or_no_show} tone="amber" testId="dash-att-absent" />
      <Chip icon={TrendingDown} label="Low avg grade" value={att.low_average_grade} tone="amber" testId="dash-att-low-grade" />
      <Chip icon={CalendarX} label="Missing this week" value={att.missing_attendance_this_week} tone="amber" testId="dash-att-missing" />
    </button>
  );
};

export default DashboardAcademicAttention;
